<template>
  <div :class="[getContainerClass, {'icon-button__container--disabled': isDisabled}]">
    <button
      :id="id"
      type="button"
      class="icon-button"
      :class="[sizeClass, themeClass]"
      :aria-label="ariaLabel"
      @click="emitCustomEvent">
      <span v-if="loading" class="icon-button__progress">
        <span class="progress fc-inline-block" />
      </span>
      <span
        v-else
        class="icon-button__icon"
        :style="iconRotationStyle">
        <FontAwesomeIcon :icon="iconName" />
      </span>
    </button>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, PropType } from '@vue/composition-api';
  import FontAwesomeIcon from '../../icons/icons';
  import {
    IconButtonSizes,
    IconButtonThemes,
    IconButtonMode,
    IconButtonProps,
    IconButtonIcon,
    ICON_BUTTON_ICONS,
  } from './types';

  export default defineComponent({
    name: 'IconButton',
    components: {
      FontAwesomeIcon,
    },
    props: {
      id: {
        type: String,
        required: false,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
        required: false,
      },
      disabled: {
        type: Boolean,
        default: false,
        required: false,
      },
      pressed: {
        type: Boolean,
        default: false,
        required: false,
      },
      mode: {
        type: String as PropType<IconButtonMode>,
        default: 'light',
        required: false,
      },
      buttonTheme: {
        type: String as PropType<IconButtonThemes>,
        required: false,
        default: 'primary',
      },
      customEventName: {
        type: String,
        required: true,
      },
      buttonSize: {
        type: String as PropType<IconButtonSizes>,
        required: false,
        default: 'medium',
      },
      ariaLabel: {
        type: String,
        required: true,
      },
      icon: {
        type: String as PropType<IconButtonIcon>,
        required: true,
      },
      iconRotation: {
        type: Number,
        required: false,
        default: undefined,
      },
    },
    setup(props: IconButtonProps, context: any) {
      const isDisabled = computed(() => props.disabled || props.loading);

      const iconConfig = computed(() => ICON_BUTTON_ICONS[props.icon]);
      const iconName = computed(() => iconConfig.value.icon);
      const iconRotationAngle = computed(() => (
        props.iconRotation !== undefined
          ? props.iconRotation
          : iconConfig.value.rotation
      ));

      const iconRotationStyle = computed(() => {
        const rotation = iconRotationAngle.value;
        if (rotation !== undefined) {
          return { transform: `rotate(${rotation}deg)` };
        }
        return {};
      });

      const sizeClass = computed(() => {
        const sizes = {
          'x-small': 'icon-button--x-small',
          small: 'icon-button--small',
          medium: 'icon-button--medium',
          large: 'icon-button--large',
        };
        return sizes[props.buttonSize as IconButtonSizes];
      });

      const themeClass = computed(() => {
        const classes = ['icon-button-layout'];

        // Add theme
        classes.push(`icon-button--${props.buttonTheme}`);

        // Add mode modifier if dark mode is specified
        if (props.mode === 'dark') {
          classes.push(`icon-button--${props.buttonTheme}--on-dark`);
        }

        // Add mode modifier if dark-white mode is specified
        if (props.mode === 'dark-white') {
          classes.push(`icon-button--${props.buttonTheme}--on-dark-white`);
        }

        // Add pressed modifier to the theme if pressed
        if (props.pressed && !isDisabled.value) {
          classes.push(`icon-button--${props.buttonTheme}--pressed`);
        }

        // Add disabled modifier to the theme if disabled
        if (isDisabled.value) {
          classes.push(`icon-button--${props.buttonTheme}--disabled`, 'icon-button--disabled');
        }

        return classes.join(' ');
      });

      return {
        emitCustomEvent() {
          context.emit(props.customEventName);
        },
        isDisabled,
        getContainerClass: computed(() => 'fc-inline-block'),
        sizeClass,
        themeClass,
        iconName,
        iconRotationAngle,
        iconRotationStyle,
      };
    },
  });
</script>

<style lang="scss" scoped>
@import "../../styles/exports";
@import "../../styles/utilities/button";

.icon-button {
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, opacity 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(&--disabled) {
    transform: scale(1.1);
  }

  &:active:not(&--disabled) {
    transform: scale(0.95);
  }

  &__container--disabled {
    cursor: not-allowed;
  }

  &--disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  &__progress {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Sizes
  &--x-small {
    width: 30px;
    height: 30px;

    svg {
      font-size: 12px;
    }
  }

  &--small {
    width: 40px;
    height: 40px;

    svg {
      font-size: 14px;
    }
  }

  &--medium {
    width: 44px;
    height: 44px;

    svg {
      font-size: 16px;
    }
  }

  &--large {
    width: 48px;
    height: 48px;

    svg {
      font-size: 18px;
    }
  }

  // Reuse button themes from _button.scss
  &--primary {
    @extend .button-primary;
  }

  &--primary--pressed {
    @extend .button-primary--pressed;
  }

  &--primary--disabled {
    @extend .button-primary--disabled;
  }

  &--secondary {
    @extend .button-secondary;
  }

  &--secondary--pressed {
    @extend .button-secondary--pressed;
  }

  &--secondary--disabled {
    @extend .button-secondary--disabled;
  }

  &--outline {
    @extend .button-outline;
  }

  &--outline--pressed {
    @extend .button-outline--pressed;
  }

  &--outline--on-dark {
    @extend .button-outline--on-dark;

    // Support disabled state on dark mode
    &.icon-button--outline--disabled {
      @extend .button-outline--on-dark;
      @extend .button-outline--disabled;
    }
  }

  &--outline--on-dark-white {
    @extend .button-outline--on-dark-white;

    // Support disabled state on dark-white mode
    &.icon-button--outline--disabled {
      @extend .button-outline--on-dark-white;
      @extend .button-outline--disabled;
    }
  }

  &--outline--disabled {
    @extend .button-outline--disabled;
  }

  &--tertiary {
    background-color: $color-white;
    color: $color-blue;
    border: 2px solid $color-blue;
  }

  &--tertiary--pressed {
    @extend .button-tertiary--pressed;
  }

  &--tertiary--disabled {
    @extend .button-tertiary--disabled;
  }
}

.progress {
  width: 12px;
  height: 12px;
}

.progress::after {
  content: " ";
  display: block;
  width: 9px;
  height: 9px;
  margin: get-spacing-level(0);
  border-radius: 50%;
  border: 2px solid currentColor;
  border-color: currentColor transparent currentColor transparent;
  animation: progress 1.2s linear infinite;
}

@keyframes progress {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
